import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { isBookOwner, isEmployee, isPlatformAdmin } from '../lib/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, Trash2, Upload, Search, Save, ArrowLeft, Check, X, 
  AlertCircle, Loader2, Image, Link, FileSpreadsheet, Sparkles,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { postBook } from '../lib/postBook';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const LANGUAGES = [
  'English', 'Hindi', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Sanskrit', 'Others'
];

const READING_AGES = [
  '4-12', '13-21', '22-30', '30+'
];

const BulkBookUpload = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [books, setBooks] = useState([createEmptyBook()]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichingRows, setEnrichingRows] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedBookIndex, setSelectedBookIndex] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const fileInputRef = useRef(null);
  const canAssignOwner = isEmployee(user?.role) || isPlatformAdmin(user?.role);
  const [prosumerOwners, setProsumerOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');

  useEffect(() => {
    if (!canAssignOwner || !token) return;
    const loadOwners = async () => {
      try {
        const res = await axios.get(`${API}/admin/prosumers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProsumerOwners(res.data || []);
      } catch (e) {
        setProsumerOwners([]);
      }
    };
    loadOwners();
  }, [canAssignOwner, token]);

  function createEmptyBook() {
    return {
      id: Date.now() + Math.random(),
      title: '',
      author: '',
      category: '',
      price: '',
      stock: '1',
      pincode: '',
      reading_age: '',
      age_category: '',
      language: 'English',
      // AI enriched fields
      description: '',
      publisher: '',
      pages: '',
      weight: '',
      size: '',
      isbn_10: '',
      isbn_13: '',
      year: '',
      cover_image: '',
      // Status
      status: 'draft', // draft, enriching, enriched, needs_review, error, saved
      error: '',
      needsReview: [],
      isManualImage: false
    };
  }

  // Fuzzy match a pasted value to the closest option in a list
  const matchOption = (value, options) => {
    if (!value) return '';
    const lower = value.toLowerCase().trim();
    // Exact match first
    const exact = options.find(o => o.toLowerCase() === lower);
    if (exact) return exact;
    // Partial match
    const partial = options.find(o => o.toLowerCase().includes(lower) || lower.includes(o.toLowerCase()));
    if (partial) return partial;
    return '';
  };

  const matchCategory = (value) => {
    const catNames = categories.map(c => c.name);
    return matchOption(value, catNames);
  };

  const matchLanguage = (value) => matchOption(value, LANGUAGES) || 'English';
  const matchReadingAge = (value) => matchOption(value, READING_AGES);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const addRow = () => {
    setBooks([...books, createEmptyBook()]);
  };

  const removeRow = (index) => {
    if (books.length === 1) {
      toast.error('At least one row is required');
      return;
    }
    const newBooks = books.filter((_, i) => i !== index);
    setBooks(newBooks);
  };

  const applyPincodeToAll = () => {
    const firstPincode = books[0]?.pincode?.trim();
    if (!firstPincode) {
      toast.error('Enter a pincode in row 1 first');
      return;
    }
    setBooks(prev => prev.map((b, idx) => idx === 0 ? b : { ...b, pincode: firstPincode }));
    toast.success(`Pincode ${firstPincode} applied to ${books.length - 1} other row(s)`);
  };

  const updateBook = (index, field, value) => {
    const newBooks = [...books];
    newBooks[index][field] = value;
    // Clear needs review for this field if manually edited
    if (newBooks[index].needsReview.includes(field)) {
      newBooks[index].needsReview = newBooks[index].needsReview.filter(f => f !== field);
    }
    setBooks(newBooks);
  };

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!clipboardData) return;

    const rows = clipboardData.split('\n').filter(row => row.trim());
    const hasMultipleCols = rows.some(r => r.includes('\t'));

    if (!hasMultipleCols && rows.length <= 1) return;

    e.preventDefault();

    const newBooks = rows.map(row => {
      const cols = row.split('\t');
      return {
        ...createEmptyBook(),
        title: cols[0]?.trim() || '',
        author: cols[1]?.trim() || '',
        category: matchCategory(cols[2]?.trim()),
        price: cols[3]?.trim() || '',
        stock: cols[4]?.trim() || '1',
        pincode: cols[5]?.trim() || '',
        language: matchLanguage(cols[6]?.trim()),
        reading_age: matchReadingAge(cols[7]?.trim()),
        age_category: matchReadingAge(cols[7]?.trim()),
        pages: cols[8]?.trim() || '',
        cover_image: cols[9]?.trim() || '',
      };
    }).filter(b => b.title);

    if (newBooks.length === 0) return;

    if (books.length === 1 && !books[0].title) {
      setBooks(newBooks);
    } else {
      setBooks([...books, ...newBooks]);
    }

    toast.success(`Pasted ${newBooks.length} book(s) from clipboard`);
  };

  const handlePasteArea = (text) => {
    if (!text.trim()) return;
    const rows = text.split('\n').filter(row => row.trim());
    const newBooks = rows.map(row => {
      const cols = row.split('\t').length > 1 ? row.split('\t') : row.split(',');
      return {
        ...createEmptyBook(),
        title: cols[0]?.trim() || '',
        author: cols[1]?.trim() || '',
        category: matchCategory(cols[2]?.trim()),
        price: cols[3]?.trim() || '',
        stock: cols[4]?.trim() || '1',
        pincode: cols[5]?.trim() || '',
        language: matchLanguage(cols[6]?.trim()),
        reading_age: matchReadingAge(cols[7]?.trim()),
        age_category: matchReadingAge(cols[7]?.trim()),
        pages: cols[8]?.trim() || '',
        cover_image: cols[9]?.trim() || '',
      };
    }).filter(b => b.title);

    if (newBooks.length === 0) {
      toast.error('No valid book data found. Check column order in instructions.');
      return;
    }

    if (books.length === 1 && !books[0].title) {
      setBooks(newBooks);
    } else {
      setBooks([...books, ...newBooks]);
    }
    toast.success(`Added ${newBooks.length} book(s) from pasted data`);
  };

  const enrichWithAI = async () => {
    const booksToEnrich = books.filter(b => b.title && b.status !== 'saved');
    
    if (booksToEnrich.length === 0) {
      toast.error('Please enter at least a title to search');
      return;
    }

    setIsEnriching(true);
    let enrichedCount = 0;
    
    for (let i = 0; i < books.length; i++) {
      const book = books[i];
      if (!book.title || book.status === 'saved') continue;
      
      setEnrichingRows(prev => new Set([...prev, book.id]));
      
      try {
        const response = await axios.post(
          `${API}/books/enrich`,
          { title: book.title, author: book.author || '', fast: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const enrichedData = response.data;
        
        const bookIndex = books.findIndex(b => b.id === book.id);
        
        if (bookIndex !== -1) {
          setBooks(prev => {
            const updated = [...prev];
            const needsReview = [];
            
            if (!updated[bookIndex].author && enrichedData.author) {
              updated[bookIndex].author = enrichedData.author;
            }
            if (!updated[bookIndex].description && enrichedData.description) {
              updated[bookIndex].description = enrichedData.description;
              if (enrichedData.confidence?.description < 0.7) needsReview.push('description');
            }
            if (!updated[bookIndex].publisher && enrichedData.publisher) {
              updated[bookIndex].publisher = enrichedData.publisher;
              if (enrichedData.confidence?.publisher < 0.7) needsReview.push('publisher');
            }
            if (!updated[bookIndex].pages && enrichedData.pages) {
              updated[bookIndex].pages = String(enrichedData.pages);
              if (enrichedData.confidence?.pages < 0.7) needsReview.push('pages');
            }
            if (!updated[bookIndex].weight && enrichedData.weight) {
              updated[bookIndex].weight = String(enrichedData.weight);
            }
            if (!updated[bookIndex].size && enrichedData.size) {
              updated[bookIndex].size = enrichedData.size;
            }
            if (!updated[bookIndex].reading_age && enrichedData.reading_age) {
              const matched = matchReadingAge(enrichedData.reading_age) || enrichedData.reading_age;
              updated[bookIndex].reading_age = matched;
              updated[bookIndex].age_category = matched;
            }
            if (!updated[bookIndex].category && enrichedData.category) {
              updated[bookIndex].category = matchCategory(enrichedData.category);
            }
            if (!updated[bookIndex].language && enrichedData.language) {
              updated[bookIndex].language = matchLanguage(enrichedData.language);
            }
            if (!updated[bookIndex].price && enrichedData.price) {
              updated[bookIndex].price = String(enrichedData.price);
              if ((enrichedData.confidence?.price ?? 0) < 0.7) needsReview.push('price');
            }
            if (!updated[bookIndex].isbn_10 && enrichedData.isbn_10) {
              updated[bookIndex].isbn_10 = enrichedData.isbn_10;
            }
            if (!updated[bookIndex].isbn_13 && enrichedData.isbn_13) {
              updated[bookIndex].isbn_13 = enrichedData.isbn_13;
            }
            if (!updated[bookIndex].year && enrichedData.year) {
              updated[bookIndex].year = String(enrichedData.year);
            }
            if (!updated[bookIndex].cover_image) {
              if (enrichedData.cover_image) {
                updated[bookIndex].cover_image = enrichedData.cover_image;
                updated[bookIndex].isManualImage = false;
              } else if (enrichedData.isbn_13) {
                updated[bookIndex].cover_image = `https://covers.openlibrary.org/b/isbn/${enrichedData.isbn_13}-L.jpg`;
                updated[bookIndex].isManualImage = false;
              } else if (enrichedData.isbn_10) {
                updated[bookIndex].cover_image = `https://covers.openlibrary.org/b/isbn/${enrichedData.isbn_10}-L.jpg`;
                updated[bookIndex].isManualImage = false;
              }
            }
            
            updated[bookIndex].needsReview = needsReview;
            updated[bookIndex].alreadyOnPlatform = enrichedData.source === 'internal';
            if (updated[bookIndex].alreadyOnPlatform) {
              updated[bookIndex].error = 'Already on MyBook.Rent — saving will ask to add another copy';
            } else {
              updated[bookIndex].error = '';
            }
            updated[bookIndex].status = needsReview.length > 0 || updated[bookIndex].alreadyOnPlatform
              ? 'needs_review'
              : 'enriched';
            
            if (!updated[bookIndex].cover_image) {
              updated[bookIndex].error = 'Cover image not found — please add manually';
              if (updated[bookIndex].status === 'enriched') updated[bookIndex].status = 'needs_review';
            }
            
            return updated;
          });
          enrichedCount++;
        }
      } catch (error) {
        const bookIndex = books.findIndex(b => b.id === book.id);
        if (bookIndex !== -1) {
          setBooks(prev => prev.map((b, idx) => idx === bookIndex ? { ...b, status: 'error', error: error.response?.data?.detail || 'Failed to enrich' } : b));
        }
      }
      
      setEnrichingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(book.id);
        return newSet;
      });
    }
    
    setIsEnriching(false);
    toast.success(`AI enrichment complete! ${enrichedCount} book(s) enriched.`);
  };

  const openImageDialog = (index) => {
    setSelectedBookIndex(index);
    setImageUrl(books[index].cover_image || '');
    setShowImageDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image too large. Please upload a cover under 3 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (selectedBookIndex !== null) {
        updateBook(selectedBookIndex, 'cover_image', reader.result);
        updateBook(selectedBookIndex, 'isManualImage', true);
      }
      setShowImageDialog(false);
      toast.success('Image uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrl = () => {
    if (selectedBookIndex !== null && imageUrl) {
      updateBook(selectedBookIndex, 'cover_image', imageUrl);
      updateBook(selectedBookIndex, 'isManualImage', true);
    }
    setShowImageDialog(false);
  };

  const validateBooks = () => {
    const errors = [];
    books.forEach((book, index) => {
      if (!book.title) errors.push(`Row ${index + 1}: Title is required`);
      if (!book.author) errors.push(`Row ${index + 1}: Author is required`);
      if (!book.category) errors.push(`Row ${index + 1}: Category is required`);
      if (!book.price || isNaN(book.price)) errors.push(`Row ${index + 1}: Valid price is required`);
    });
    return errors;
  };

  const saveAllBooks = async () => {
    const errors = validateBooks();
    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">Validation Errors:</p>
          <ul className="text-sm mt-1">
            {errors.slice(0, 5).map((e, i) => <li key={i}>• {e}</li>)}
            {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
          </ul>
        </div>
      );
      return;
    }

    setIsSaving(true);
    let savedCount = 0;
    let errorCount = 0;

    for (let i = 0; i < books.length; i++) {
      const book = books[i];
      if (book.status === 'saved') continue;

      try {
        // Avoid huge base64 payloads that freeze the browser/API
        let coverImage = book.cover_image || null;
        if (coverImage && coverImage.startsWith('data:') && coverImage.length > 300000) {
          coverImage = null;
          toast.warning(`Row ${i + 1}: cover image too large — saved without cover. Use a URL instead.`);
        }

        const bookData = {
          title: book.title,
          author: book.author,
          category: book.category,
          price: parseFloat(book.price),
          stock: parseInt(book.stock) || 1,
          pincode: book.pincode || user?.pincode || '',
          description: book.description || null,
          publisher: book.publisher || null,
          pages: book.pages ? parseInt(book.pages) : null,
          language: book.language || 'English',
          cover_image: coverImage,
          reading_age: book.reading_age || null,
          age_category: book.reading_age || null,
          weight_gms: book.weight ? parseInt(book.weight) : null,
          size_cms: book.size || null,
          isbn_10: book.isbn_10 || null,
          isbn_13: book.isbn_13 || null,
          isbn: book.isbn_13 || book.isbn_10 || null,
          pickup_city: user?.city || '',
          rental_price_per_week: Math.round(parseFloat(book.price) * 0.30),
          ...(canAssignOwner && selectedOwnerId ? { owner_id: selectedOwnerId } : {})
        };

        await postBook(API, token, bookData);

        setBooks(prev => prev.map((b, idx) => idx === i ? { ...b, status: 'saved', error: '' } : b));
        savedCount++;
      } catch (error) {
        if (error?.cancelled) {
          setBooks(prev => prev.map((b, idx) => idx === i ? { ...b, status: 'draft', error: 'Skipped — already listed' } : b));
          continue;
        }
        const detail = error.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to save');
        setBooks(prev => prev.map((b, idx) => idx === i ? { ...b, status: 'error', error: msg } : b));
        errorCount++;
      }
    }

    setIsSaving(false);
    
    if (savedCount > 0) {
      toast.success(`Successfully saved ${savedCount} book(s)`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to save ${errorCount} book(s)`);
    }
  };

  const getStatusBadge = (book) => {
    switch (book.status) {
      case 'saved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>;
      case 'enriched':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1"><Sparkles className="h-3 w-3" /> Enriched</span>;
      case 'needs_review':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Review</span>;
      case 'error':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs flex items-center gap-1"><X className="h-3 w-3" /> Error</span>;
      case 'enriching':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Enriching</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Draft</span>;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--background)' }}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center space-y-4 border border-gray-100">
          <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ backgroundColor: '#E8E4C9' }}>
            <FileSpreadsheet className="h-8 w-8" style={{ color: 'var(--primary)' }} />
          </div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Bulk Book Upload</h2>
          <p className="text-sm text-gray-600">
            Please log in with your Prosumer, Employee, or Executive Leader account to access bulk book uploads.
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <Button
              onClick={() => navigate('/login?redirect=/admin/bulk-upload')}
              style={{ backgroundColor: 'var(--primary)', color: 'white' }}
              className="w-full h-11 text-base font-semibold"
            >
              Log in to continue
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="w-full h-11">
              Back to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isBookOwner(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--background)' }}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center space-y-4 border border-gray-100">
          <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center bg-amber-100">
            <AlertCircle className="h-8 w-8 text-amber-700" />
          </div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Access Restricted</h2>
          <p className="text-sm text-gray-600">
            Bulk book upload is reserved for book owners (Prosumers, Employees, and Platform Admins). Your account is currently logged in as a Borrower.
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <Button
              onClick={() => navigate('/customer-dashboard')}
              style={{ backgroundColor: 'var(--primary)', color: 'white' }}
              className="w-full h-11 font-semibold"
            >
              Go to Customer Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="w-full h-11">
              Browse Books
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate(isPlatformAdmin(user?.role) ? '/super-admin' : '/admin');
                  }
                }}
                className="text-gray-600"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>
                  <FileSpreadsheet className="h-6 w-6 inline mr-2" />
                  Bulk Book Upload
                </h1>
                <p className="text-sm text-gray-500">Add multiple books with AI-powered enrichment</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={enrichWithAI}
                disabled={isEnriching || books.every(b => !b.title)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="enrich-ai-btn"
              >
                {isEnriching ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enriching...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Search & Enrich with AI</>
                )}
              </Button>
              <Button
                onClick={saveAllBooks}
                disabled={isSaving || books.every(b => b.status === 'saved')}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-all-btn"
              >
                {isSaving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save All Books</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions + Paste Area */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {canAssignOwner && (
          <div className="mb-4 p-4 rounded-xl border border-blue-200 bg-blue-50/70 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <Label className="text-sm font-semibold text-blue-900">Who owns these books?</Label>
              <p className="text-xs text-blue-700">
                Employees and Executive Leaders can bulk-upload books for a Prosumer. Select the owner before clicking "Save All Books".
              </p>
            </div>
            <div className="w-full sm:w-80">
              <Select value={selectedOwnerId || '__self__'} onValueChange={(v) => setSelectedOwnerId(v === '__self__' ? '' : v)}>
                <SelectTrigger data-testid="bulk-owner-select" className="bg-white">
                  <SelectValue placeholder="Myself" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">Myself ({user?.name})</SelectItem>
                  {prosumerOwners.filter(o => o.id !== user?.id).map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name} ({o.email || o.phone || o.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">How to use:</h3>
            <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
              <li>Enter titles (author optional) or <strong>paste from Excel</strong></li>
              <li>Click <strong>"Search & Enrich with AI"</strong> — title-only search, then web if not in catalogue. Pincode is not needed.</li>
              <li>Review yellow-highlighted fields that may need verification</li>
              <li>Click <strong>"Save All Books"</strong> to publish (pincode defaults to your profile)</li>
            </ol>
            <p className="text-xs text-blue-600 mt-2">Column order: Title, Author, Category, Price, Stock, Pincode (optional), Language, Reading Age, Pages, Cover Image URL</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Paste from Excel / CSV
            </h3>
            <Textarea
              placeholder={"Paste rows here from Excel...\ne.g.:\nThe Alchemist\tPaulo Coelho\tFiction\t350\t1\nAtomic Habits\tJames Clear\tSelf-Development\t400\t2"}
              className="text-sm h-24 bg-white"
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData('text');
                e.target.value = text;
                handlePasteArea(text);
                e.target.value = '';
              }}
              data-testid="paste-from-excel"
            />
            <p className="text-xs text-amber-600 mt-1">Paste tab-separated or comma-separated data. One book per row.</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto" onPaste={handlePaste}>
            <table className="w-full min-w-[1400px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-10">#</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase min-w-[280px]">Title *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Author *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Price (₹) *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stock</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pincode</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Language</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reading Age</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cover</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pages</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {books.map((book, index) => (
                  <tr 
                    key={book.id} 
                    className={`
                      ${book.status === 'saved' ? 'bg-green-50' : ''}
                      ${book.status === 'error' ? 'bg-red-50' : ''}
                      ${enrichingRows.has(book.id) ? 'bg-purple-50' : ''}
                      hover:bg-gray-50 transition-colors
                    `}
                  >
                    <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2">
                      {enrichingRows.has(book.id) ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" /> Enriching
                        </span>
                      ) : (
                        getStatusBadge(book)
                      )}
                      {book.error && <p className="text-xs text-red-500 mt-1">{book.error}</p>}
                    </td>
                    <td className="px-3 py-2 min-w-[280px]">
                      <Input
                        value={book.title}
                        onChange={(e) => updateBook(index, 'title', e.target.value)}
                        placeholder="Book title"
                        className={`h-9 text-sm w-full min-w-[260px] ${book.needsReview.includes('title') ? 'border-yellow-400 bg-yellow-50' : ''}`}
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={book.author}
                        onChange={(e) => updateBook(index, 'author', e.target.value)}
                        placeholder="Author name"
                        className="h-9 text-sm"
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <textarea
                        value={book.description || ''}
                        onChange={(e) => updateBook(index, 'description', e.target.value)}
                        placeholder="Filled by Enrich, or type a summary"
                        className="h-16 w-56 text-sm border rounded-md px-2 py-1"
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={book.category}
                        onValueChange={(value) => updateBook(index, 'category', value)}
                        disabled={book.status === 'saved'}
                      >
                        <SelectTrigger className="h-9 text-sm w-32">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={book.price}
                        onChange={(e) => updateBook(index, 'price', e.target.value)}
                        placeholder="₹"
                        className="h-9 text-sm w-20"
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={book.stock}
                        onChange={(e) => updateBook(index, 'stock', e.target.value)}
                        placeholder="1"
                        className="h-9 text-sm w-16"
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={book.pincode}
                        onChange={(e) => updateBook(index, 'pincode', e.target.value)}
                        placeholder={user?.pincode || 'optional'}
                        className="h-9 text-sm w-24"
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={book.language}
                        onValueChange={(value) => updateBook(index, 'language', value)}
                        disabled={book.status === 'saved'}
                      >
                        <SelectTrigger className="h-9 text-sm w-28">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map(lang => (
                            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={book.reading_age}
                        onValueChange={(value) => { updateBook(index, 'reading_age', value); updateBook(index, 'age_category', value); }}
                        disabled={book.status === 'saved'}
                      >
                        <SelectTrigger className="h-9 text-sm w-28">
                          <SelectValue placeholder="Age" />
                        </SelectTrigger>
                        <SelectContent>
                          {READING_AGES.map(age => (
                            <SelectItem key={age} value={age}>{age}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      {book.cover_image ? (
                        <div className="flex items-center space-x-2">
                          <img 
                            src={book.cover_image} 
                            alt="Cover" 
                            className="h-10 w-8 object-cover rounded border"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openImageDialog(index)}
                            disabled={book.status === 'saved'}
                            className="h-8 px-2"
                          >
                            <Image className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openImageDialog(index)}
                          disabled={book.status === 'saved'}
                          className="h-8 text-xs"
                        >
                          <Upload className="h-3 w-3 mr-1" /> Add
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={book.pages}
                        onChange={(e) => updateBook(index, 'pages', e.target.value)}
                        placeholder="Pages"
                        className={`h-9 text-sm w-20 ${book.needsReview.includes('pages') ? 'border-yellow-400 bg-yellow-50' : ''}`}
                        disabled={book.status === 'saved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeRow(index)}
                        disabled={book.status === 'saved' || books.length === 1}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Add Row Button */}
          <div className="p-4 border-t bg-gray-50">
            <Button
              variant="outline"
              onClick={addRow}
              className="w-full border-dashed"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Another Book
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Total: {books.length} books</span>
            <span className="text-green-600">Saved: {books.filter(b => b.status === 'saved').length}</span>
            <span className="text-yellow-600">Needs Review: {books.filter(b => b.status === 'needs_review').length}</span>
            <span className="text-red-600">Errors: {books.filter(b => b.status === 'error').length}</span>
          </div>
        </div>
      </div>

      {/* Image Upload Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: 'white' }}>
          <DialogHeader>
            <DialogTitle className="text-gray-800">Add Cover Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Option */}
            <div>
              <Label className="text-gray-700">Upload from device</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full mt-2 h-20 border-dashed"
              >
                <div className="flex flex-col items-center">
                  <Upload className="h-6 w-6 mb-1 text-gray-500" />
                  <span className="text-sm">Click to upload</span>
                  <span className="text-xs text-gray-400">Max 5MB, JPG/PNG/WebP</span>
                </div>
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or</span>
              </div>
            </div>

            {/* URL Option */}
            <div>
              <Label className="text-gray-700">Enter image URL</Label>
              <div className="flex space-x-2 mt-2">
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1"
                />
                <Button onClick={handleImageUrl} disabled={!imageUrl}>
                  <Link className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>

            {/* Preview */}
            {(imageUrl || (selectedBookIndex !== null && books[selectedBookIndex]?.cover_image)) && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <img
                  src={imageUrl || books[selectedBookIndex]?.cover_image}
                  alt="Preview"
                  className="max-h-40 mx-auto rounded"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkBookUpload;
